/**
 * PfChipStrip — configurator-only chip-tab strip (Phase C-A / C3).
 *
 * Replaces the legacy MUI <Tabs> rendering on the Configuration page. Each
 * chip carries a leading 16x16 status badge (filled-checkmark when the user
 * has confirmed the tab (complete while viewed); empty ring otherwise) and is wired into
 * a WAI-ARIA `tablist` with roving-tabindex keyboard nav.
 *
 * Validity-override slot (`PfChipStripItem.validity`) is implemented now but
 * has no consumer yet — Phase C-A.4 (sub-tab strips for energy / consumption
 * cert) will wire it.
 *
 * Configurator-only by design — don't reuse outside this step without a
 * design pass. The visual / a11y semantics are tuned to the configurator's
 * "active tuple + completion-progression" model and won't translate cleanly
 * to a generic tab strip.
 */

import React, { useCallback, useRef } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'

import {
  BEIGE_500,
  BEIGE_600,
  BEIGE_700,
  BLUE_100,
  BLUE_600,
  BOX_SHADOW_XS,
  CORAL_100,
  CORAL_600,
  GRAY_400,
  GRAY_900,
  GREEN_600,
  WHITE
} from 'constants/styling/theme'
import { MUITooltip } from 'components/common/MUITooltip'

import { usePfStore, type PfStoreState } from '../../store'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PfChipStripItem {
  /** Stable key, e.g. `${cartItemId}|${flowId}`. */
  id: string
  label: string
  /** Adapter `isComplete()` return — feeds the badge ring color. */
  isComplete: boolean
  /**
   * `cartItemUserConfirmed[cartItemId]?.[flowId]` from the store. Combined
   * with `isComplete` to decide whether to render the filled GREEN_600 check
   * badge. A chip is considered "completed" iff `isComplete && userConfirmed`.
   */
  userConfirmed: boolean
  /**
   * Sub-tab validity override — used by sub-strips (energy / consumption
   * certificate). When set, replaces the standard badge with a green check
   * (valid), red dot (invalid), or empty ring (untouched).
   */
  validity?: 'valid' | 'invalid' | 'untouched'
}

export interface PfChipStripProps {
  items: PfChipStripItem[]
  activeId: string
  onSelect: (id: string) => void
  /** `tablist` aria-label for screen readers. */
  ariaLabel: string
  /**
   * Optional per-chip completeness subscription (MODULE-CONTRACT §4a). When
   * provided, each chip subscribes to `usePfStore(completeSelector(item.id))`
   * for its badge + connector `isComplete` instead of the static
   * `item.isComplete` — lets the configurator drive completeness from the pure
   * `selectFlowComplete` selector with NO render-time recompute in the parent.
   * Backward-compatible: omit it and chips use the static `item.isComplete`.
   */
  completeSelector?: (itemId: string) => (state: PfStoreState) => boolean
  /** Give every chip whose form is not complete-and-confirmed the orange border, and
   *  the ACTIVE one the orange unfinished badge, so the client sees what is still to do.
   *  Off by default. A strip that passes its own `validity` keeps its badge, and an
   *  `invalid` item gets the same orange border. */
  markIncomplete?: boolean
}

// ─── Constants ────────────────────────────────────────────────────────────────

const TOOLTIP_LENGTH_THRESHOLD = 24

// ─── Sub-component: leading badge ─────────────────────────────────────────────

interface BadgeProps {
  isComplete: boolean
  userConfirmed: boolean
  validity?: PfChipStripItem['validity']
}

const Badge: React.FC<BadgeProps> = ({ isComplete, userConfirmed, validity }) => {
  // Validity prop overrides the default isComplete/userConfirmed logic
  // (used by sub-tab strips that track per-section validity instead of
  // user-confirmed state).
  if (validity === 'valid') {
    return (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '1.6rem',
          height: '1.6rem',
          borderRadius: '50%',
          backgroundColor: GREEN_600,
          flexShrink: 0,
        }}
      >
        <CheckRoundedIcon sx={{ fontSize: '1.2rem', color: WHITE }} />
      </Box>
    )
  }

  if (validity === 'invalid') {
    return (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '1.6rem',
          height: '1.6rem',
          borderRadius: '50%',
          border: `1px solid ${CORAL_600}`,
          flexShrink: 0,
        }}
      >
        <Box
          component="span"
          sx={{
            width: '0.8rem',
            height: '0.8rem',
            borderRadius: '50%',
            backgroundColor: CORAL_600,
          }}
        />
      </Box>
    )
  }

  // Default path — driven by isComplete + userConfirmed. Green, the same
  // "this section is done" signal the sub-strips give with `validity: 'valid'`.
  if (isComplete && userConfirmed) {
    return (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '1.6rem',
          height: '1.6rem',
          borderRadius: '50%',
          backgroundColor: GREEN_600,
          flexShrink: 0,
        }}
      >
        <CheckRoundedIcon sx={{ fontSize: '1.2rem', color: WHITE }} />
      </Box>
    )
  }

  // Untouched / incomplete — empty ring
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        width: '1.6rem',
        height: '1.6rem',
        borderRadius: '50%',
        border: `1px solid ${GRAY_400}`,
        flexShrink: 0,
        boxSizing: 'border-box',
      }}
    />
  )
}

// ─── Sub-component: connector rule ────────────────────────────────────────────

interface ConnectorProps {
  isPriorComplete: boolean
}

const Connector: React.FC<ConnectorProps> = ({ isPriorComplete }) => (
  <Box
    component="span"
    aria-hidden="true"
    sx={{
      width: '1.2rem',
      height: 0,
      borderTop: `1px solid ${isPriorComplete ? GRAY_900 : BEIGE_700}`,
      flexShrink: 0,
    }}
  />
)

// ─── Sub-component: chip ──────────────────────────────────────────────────────

interface ChipProps {
  item: PfChipStripItem
  isActive: boolean
  /** Still to configure: drawn with the orange border. */
  isPending?: boolean
  isInteractive: boolean
  /** ID of THIS chip's owned panel (per WAI-ARIA tablist — `aria-controls`
   *  must reference the chip's own panel, not the active one). */
  panelId: string
  onSelect: (id: string) => void
  onKeyNav: (e: React.KeyboardEvent<HTMLDivElement>, id: string) => void
  registerRef: (id: string, el: HTMLDivElement | null) => void
}

const Chip: React.FC<ChipProps> = ({
  item, isActive, isPending = false, isInteractive, panelId, onSelect, onKeyNav, registerRef,
}) => {
  const handleClick = useCallback(() => {
    if (!isInteractive) return
    onSelect(item.id)
  }, [isInteractive, onSelect, item.id])

  const isDone = item.isComplete && item.userConfirmed
  const showTooltip = item.label.length > TOOLTIP_LENGTH_THRESHOLD

  const chipNode = (
    <Box
      ref={(el: HTMLDivElement | null) => registerRef(item.id, el)}
      role="tab"
      aria-selected={isActive}
      aria-controls={panelId}
      id={`pf-chip-tab-${item.id}`}
      tabIndex={isInteractive ? (isActive ? 0 : -1) : -1}
      onClick={handleClick}
      onKeyDown={(e) => isInteractive && onKeyNav(e, item.id)}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.8rem',
        height: '4rem',
        px: '1.2rem',
        // The chip you are on stays blue; one still to do elsewhere is tinted orange (QA-664).
        backgroundColor: isActive ? BLUE_100 : isPending ? CORAL_100 : WHITE,
        // Done outranks active on the border — the blue fill still says which chip
        // you are on. The active chip otherwise stays blue; the others still to do are orange.
        border: `1px solid ${isDone ? GREEN_600 : isActive ? BLUE_600 : isPending || item.validity === 'invalid' ? CORAL_600 : BEIGE_600}`,
        borderRadius: '0.8rem',
        boxShadow: BOX_SHADOW_XS,
        cursor: isInteractive ? 'pointer' : 'default',
        userSelect: 'none',
        transition: 'background-color 120ms, border-color 120ms',
        flexShrink: 0,
        '&:hover': isInteractive && !isActive ? { backgroundColor: BEIGE_500 } : undefined,
        '&:focus-visible': {
          outline: `2px solid ${BLUE_600}`,
          outlineOffset: '2px',
        },
      }}
    >

      <Badge
        isComplete={item.isComplete}
        userConfirmed={item.userConfirmed}
        validity={item.validity}
      />

      <Typography
        component="span"
        variant="text-sm"
        fontWeight={500}
        color={GRAY_900}
        sx={{ whiteSpace: 'nowrap' }}
      >
        {item.label}
      </Typography>

    </Box>
  )

  if (showTooltip) {
    return (
      <MUITooltip content={item.label} placement="top">
        {chipNode}
      </MUITooltip>
    )
  }

  return chipNode
}

// ─── Sub-component: strip entry (connector + chip; per-chip completeness sub) ──

interface ChipStripEntryProps {
  item: PfChipStripItem
  /** The chip to the left, if any — its completeness colours the connector. */
  prior?: PfChipStripItem
  isActive: boolean
  isInteractive: boolean
  completeSelector?: (itemId: string) => (state: PfStoreState) => boolean
  /** Give every chip whose form is not complete-and-confirmed the orange border, and
   *  the ACTIVE one the orange unfinished badge, so the client sees what is still to do.
   *  Off by default. A strip that passes its own `validity` keeps its badge, and an
   *  `invalid` item gets the same orange border. */
  markIncomplete?: boolean
  onSelect: (id: string) => void
  onKeyNav: (e: React.KeyboardEvent<HTMLDivElement>, id: string) => void
  registerRef: (id: string, el: HTMLDivElement | null) => void
}

/**
 * One strip position: the connector rule (except the first) plus the chip.
 * Extracted so each chip can make its OWN `usePfStore` subscription — a hook
 * call that is illegal inside the parent's `items.map()` (rules of hooks) but
 * legal here because this is a component. Both subscriptions are unconditional;
 * the selector argument merely varies, and each falls back to the static
 * `item.isComplete` when no `completeSelector` is supplied.
 */
const ChipStripEntry: React.FC<ChipStripEntryProps> = ({
  item, prior, isActive, isInteractive, completeSelector, markIncomplete,
  onSelect, onKeyNav, registerRef,
}) => {
  const isComplete = usePfStore(
    completeSelector ? completeSelector(item.id) : () => item.isComplete
  )
  const priorComplete = usePfStore(
    completeSelector && prior ? completeSelector(prior.id) : () => (prior?.isComplete ?? false)
  )

  // The orange dot stays on the chip you are standing on, so the strip does not open as a wall of
  // dots; every chip still to do gets the orange border instead (QA-664). An explicit `validity`
  // (the certificate sub-strips) still wins.
  const isPending = !!markIncomplete && !(isComplete && item.userConfirmed)
  const validity = item.validity ?? (isPending && isActive ? 'invalid' : undefined)

  return (
    <>

      {prior &&
        <Connector isPriorComplete={!!(priorComplete && prior.userConfirmed)} />
      }

      <Chip
        item={{ ...item, isComplete, validity }}
        isActive={isActive}
        isPending={isPending}
        isInteractive={isInteractive}
        panelId={`pf-chip-panel-${item.id}`}
        onSelect={onSelect}
        onKeyNav={onKeyNav}
        registerRef={registerRef}
      />

    </>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * @component PfChipStrip
 * Horizontal chip-tab strip with WAI-ARIA tablist semantics + completion
 * badge. Configurator-only — see file header.
 */
export const PfChipStrip: React.FC<PfChipStripProps> = ({
  items, activeId, onSelect, ariaLabel, completeSelector, markIncomplete,
}) => {
  const chipRefs = useRef<Record<string, HTMLDivElement | null>>({})

  const registerRef = useCallback((id: string, el: HTMLDivElement | null) => {
    chipRefs.current[id] = el
  }, [])


  const isInteractive = items.length > 1

  const focusAndSelect = useCallback((id: string) => {
    onSelect(id)
    // Move focus to the newly-active chip on next tick — DOM hasn't repainted yet.
    requestAnimationFrame(() => {
      chipRefs.current[id]?.focus()
    })
  }, [onSelect])

  const handleKeyNav = useCallback((e: React.KeyboardEvent<HTMLDivElement>, currentId: string) => {
    if (items.length <= 1) return
    const idx = items.findIndex((i) => i.id === currentId)
    if (idx === -1) return

    let nextIdx = idx
    switch (e.key) {
      case 'ArrowRight':
        nextIdx = (idx + 1) % items.length
        break
      case 'ArrowLeft':
        nextIdx = (idx - 1 + items.length) % items.length
        break
      case 'Home':
        nextIdx = 0
        break
      case 'End':
        nextIdx = items.length - 1
        break
      case 'Enter':
      case ' ':
        // Re-confirm selection — same chip, no focus shift.
        e.preventDefault()
        onSelect(currentId)
        return
      default:
        return
    }
    e.preventDefault()
    focusAndSelect(items[nextIdx].id)
  }, [items, focusAndSelect, onSelect])

  return (
    <Box sx={{ position: 'relative', mb: 2.4 }}>

      <Stack
        role="tablist"
        aria-label={ariaLabel}
        aria-orientation="horizontal"
        direction="row"
        alignItems="center"
        gap={0}
        flexWrap="nowrap"
        sx={{
          overflowX: 'auto',
          // No scrollbar and no fade: the strip scrolls like the catalogue's
          // category tabs and is simply cut at the edge, rather than trailing
          // off into the background.
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {items.map((item, idx) => (
          <ChipStripEntry
            key={item.id}
            item={item}
            prior={idx > 0 ? items[idx - 1] : undefined}
            isActive={item.id === activeId}
            isInteractive={isInteractive}
            completeSelector={completeSelector}
            markIncomplete={markIncomplete}
            onSelect={onSelect}
            onKeyNav={handleKeyNav}
            registerRef={registerRef}
          />
        ))}
      </Stack>

    </Box>
  )
}
